import { Injectable } from '@angular/core';
import {
  ProductSearchCriteria, ProductSearchRequest, ProductSearchListRequest
} from '../../shared/models/product-search-criteria';
import { HttpService } from '../../core/services/http/http.service';
import {
  SearchResponse,
  ProductRealTimeRequest,
  ValidateProductRequest
} from '../../shared/models/product';
import { ParmedApiService as ParmedApi } from '../../core/api/parmed-api.service';
import { UserService } from '../../core/services/user.service';
import { ProductConstant } from './product-messages';
import { execute } from './products.service-helper';
import { AppCacheService } from '../../core/services/cache/app-cache.service'; 
import { CacheConstant } from '../../core/constants/app.constant';
import { AppConfigService } from '../../core/services/init/app-config.service';
import { Observable } from 'rxjs';
import { StaticService } from '../../core/services/static/static.service';
import { StaticConstants } from '../../core/constants/static.constants';
import { HttpHeaders } from '@angular/common/http';

@Injectable()
export class ProductsService {
   
  /***************Constructor***************/
  envProperties = this.appCacheService.get(CacheConstant.Env_Properties);
  buid = this.envProperties.buId;
  elasticSearchFlag: boolean = this.envProperties.elasticSearchFlag ? this.envProperties.elasticSearchFlag.toBoolean() : false;
  constructor(
    private http: HttpService,
    private userService: UserService,
    private api: ParmedApi, 
    private appCacheService: AppCacheService,
    private configService: AppConfigService,
    private staticService: StaticService
  ) {}
  /***************Constructor***************/

  /***************Helper Methods***************/

  /*
  This method will be used in Product Detail Screen and Search Screen to display in/out of stock icon.
  Allocated quantity should not be considered to display green or red icon.
  */
  getStock(product: any) {
    let stock = 0;
    const allocatedQuantity = product.allocatedQuantity;
    
    if (allocatedQuantity > 0) {
      stock = allocatedQuantity;
    } else if (allocatedQuantity === 0 || allocatedQuantity <= 0) {
      /* Stock will be set to 0 if allocatedQuantity is 0 or -2 meaning
      either the customer has used all the allocations for the day
      or Allocation is set up, but error with allocation (-2 SAP return value) */
      stock = 0;
    }

    return stock;
  }

  getLowQuantityProducts(products: Array<any>) {
    return products.filter(
      product => product.cartQuantity > this.getOnHandStock(product)
    );
  }

  getExcludedProducts(products: Array<any>) {
    return products.filter(
      product => product.allocatedQuantity && this.isExcluded(product)
    );
  }

  getOutofStockProducts(products: Array<any>) {
    if(this.envProperties.buId === 1) {
      return products.filter(
        product => (product.allocatedQuantity === undefined || this.getStock(product) <= 0) && !this.isExcluded(product)
      );
    }
    else if(this.envProperties.buId === 2) {
      return products.filter(
        product => (product.allocatedQuantity === undefined || this.getStock(product) < 0) && !this.isExcluded(product)
      );
    }
   
  }

  isExcluded(product: any) {
    return product.allocatedQuantity === -2 || product.allocatedQuantity === -3;
  }

  isAvailable(product: any) {
    return (
      this.getStock(product) > 0 &&
      product.allocatedQuantity !== 0 &&
      !this.isExcluded(product)
    );
  }

  isAvailableForBackOrder(product: any) {
    return (
      !this.isExcluded(product)
    );
  }

  isProductHazardous(product: any) {
    return this.containsValue(this.staticService.masterEntity.get(StaticConstants.MasterEntityKey.ProductHazardousCodes).value, product.specialHandling);
  }

  
  isProductRefrigerated(product: any) {
    return ((product.temperature) ?  !this.containsValue(this.staticService.masterEntity.get(StaticConstants.MasterEntityKey.ProductNonRefrigeratedCodes).value, product.temperature) : false);
  }

  private containsValue(commaSeparatedStr: string, value: string) {
    let contains = false;
    if (commaSeparatedStr) {
      let valuesArray = commaSeparatedStr.split(',');
      //const hazardValues = [2, 5, 6, 8];
      if (value) {
        //value = parseInt(value, 0);
        contains = valuesArray.indexOf(value) > -1;
      }  
    }
    return contains;
  }
  
  isSubscriptionAvailable(product: any) {
    return (
      product.allocatedQuantity !== 0 &&
      !this.isExcluded(product) &&
      !this.isExcludedUnitPrice(product)
    );
  }

  isExcludedUnitPrice(product: any) {
    return product.price === undefined || product.price === null || product.price <= 0;
  }

  getAvailabilityMessage(product: any): string {
    let message = '';

    if (!product.isAvailable) {
      message = this.isExcluded(product)
        ? ProductConstant.Messages.ProductAvailabilityMessage.NotAvailable
        : ProductConstant.Messages.ProductAvailabilityMessage.OutofStock;
    }

    return message;
  }

  /***************Helper Methods***************/

  /***************Service Methods***************/

  search(criteria: ProductSearchCriteria, searchRequest: ProductSearchRequest) {
    const defaultAccount = this.userService.getDefaultAccount();
    criteria.billToNumber = defaultAccount.accountBillToNum;
    criteria.userDetailNum = defaultAccount.userDetailNum;
    criteria = Object.assign(
      criteria,
      this.userService.getDefaultAccSoldtoShipToObj()
    );

    searchRequest.userDetailNum = defaultAccount.userDetailNum;
    searchRequest = Object.assign(
      searchRequest,
      this.userService.getDefaultAccSoldtoShipToObj()
    );
    

    // return this.http.post<SearchResponse>(
    //   this.api.Config.SEARCH_PRODUCTS_URL,
    //   criteria
    // );

    const isBarCodeScanned = this.appCacheService.get('BarCodeScanned');

    if (isBarCodeScanned) {
      this.appCacheService.set('BarCodeScanned', 'false');
      return execute(this.http, this.api.Product.BARCODE_PRODUCT_SEARCH_URL, searchRequest, undefined, this.configService);
    }
    return execute(this.http, this.api.Product.PRODUCT_SEARCH_URL, searchRequest, undefined, this.configService);
  }
  searchList(criteria: ProductSearchCriteria, searchRequest: ProductSearchListRequest) {
    const defaultAccount = this.userService.getDefaultAccount();
    criteria.billToNumber = defaultAccount.accountBillToNum;
    criteria.userDetailNum = defaultAccount.userDetailNum;
    criteria = Object.assign(
      criteria,
      this.userService.getDefaultAccSoldtoShipToObj()
    );

    searchRequest.userDetailNum = defaultAccount.userDetailNum;
    searchRequest = Object.assign(
      searchRequest,
      this.userService.getDefaultAccSoldtoShipToObj()
    );
    return execute(this.http, this.api.Product.PRODUCT_SEARCH_LIST_URL, searchRequest, undefined, this.configService);
  }

  searchProductTypeForward(value: String) {
    if (this.elasticSearchFlag) {
      return this.http.get(this.api.Product.PRODUCT_SUGGESTION_URL + value, { headers: { ignoreLoadingBar: '' } });
    }
    else {
      return this.http.get(this.api.Config.GET_PRODUCT_TYPEFORWARD_URL + value);
    }
  }

  getFavotiesList(userDetailNum: number) {
    const request = {
      buId: this.envProperties.buId,
      userDetailNum: userDetailNum
    };

    return this.http.post<any>(
      this.api.Config.GET_FAVORITES_URL, request
    );
  }

  getFavProducts(request: any) {
    return this.http.post<any>(
      this.api.Config.GET_FAVORITE_PRODUCTS_URL, request
    );
  }

  getWatchListProducts(request: any) {
    return this.http.post<any>(
      this.api.Config.GET_WATCHLIST_PRODUCTS_URL, request
    );
  }

  createFavList(listName: string) {
    const defaultAccount = this.userService.getDefaultAccount();
    const request = {
      buid: this.envProperties.buId,
      name: listName,
      userDetailNum: defaultAccount.userDetailNum
    };
    return this.http.post<any>(
      this.api.Config.CREATE_FAVORITE_LIST_URL, request
    );
  }

  deleteFavList(favId: number) {
    const defaultAccount = this.userService.getDefaultAccount();
    const request = {
      buid: this.envProperties.buId,
      favId: favId,
      userDetailNum: defaultAccount.userDetailNum
    };
    return this.http.post<any>(
      this.api.Config.DELETE_FAVORITE_LIST_URL, request
    );
  }

  deleteProductFavList(request) {
    return this.http.post<any>(
      this.api.Config.DELETE_PRODUCT_FROM_FAVORITE_LIST_URL, request
    );
  }

  addProductToFavList(request: any) {
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    request.soldToNum = defaultAccount.soldToNum;
    request.shipToNum = defaultAccount.shipToNum;
    request.buid = this.envProperties.buId;
    return this.http.post<any>(
      this.api.Config.ADD_PRODUCT_TO_FAVORITE_LIST_URL, request
    );
  }

  renameFavList(request: any) {
    const defaultAccount = this.userService.getDefaultAccount();
    request.userDetailNum = defaultAccount.userDetailNum;

    return this.http.post<any>(
      this.api.Config.RENAME_FAVORITE_LIST_URL, request
    );
  }

  moveFavProduct(request: any) {
    const defaultAccount = this.userService.getDefaultAccount();
    request.userDetailNum = defaultAccount.userDetailNum;

    return this.http.post<any>(
      this.api.Config.MOVE_FAVORITE_PRODUCT_URL, request
    );
  }
  addProdToWatchList(request: any) {
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    request.soldToNum = defaultAccount.soldToNum;
    request.shipToNum = defaultAccount.shipToNum;
    const getUserDetailNum = this.userService.getDefaultAccount();
    request.userDetailNum = getUserDetailNum.userDetailNum;
    request.buid = this.envProperties.buId;
    return this.http.post<any>(
      this.api.Config.ADD_PRODUCT_TO_WATCH_LIST_URL, request
    );
  }
  deleteProdFromWatchList(request: any) {
    return this.http.post<any>(
      this.api.Config.DELETE_PRODUCT_FROM_WATCH_LIST_URL, request
    ); 
  }
  getCatalogList(criteria: any) {
    const defaultAccount = this.userService.getDefaultAccount();
    criteria.userDetailNum = defaultAccount.userDetailNum;
    criteria = Object.assign(
      criteria,
      this.userService.getDefaultAccSoldtoShipToObj()
    );
    return this.http.post<SearchResponse>(
      this.api.Config.GET_PRODUCTS_BY_CATALOG_ID_URL,
      criteria
    );
  }

  getInventoryPrice(request: ProductRealTimeRequest) {
    // return this.http.post<ProductRealTimeResponseItem[]>(
    //   this.api.Config.GET_PRODUCTS_INV_PRICE_URL,
    //   request
    // );
    return execute(this.http,
      this.api.Config.GET_PRODUCTS_INV_PRICE_URL,
      request, undefined, this.configService
    );
  }

  validate(validateProductRequest: ValidateProductRequest) {
    validateProductRequest = Object.assign(
      validateProductRequest,
      this.userService.getDefaultAccSoldtoShipToObj()
    );
    return this.http.post(
      this.api.Config.VALIDATE_PRODUCTS_URL,
      validateProductRequest
    );
  }

  getProductImage(imageRequest: any) {
    // return this.http.post<any[]>(
    //   this.api.Config.GET_PRODUCT_IMAGES_URL,
    //   imageRequest,
    //   AppUtilService.getHttpDefaultOptions()
    // );
    return execute(this.http,
      this.api.Config.GET_PRODUCT_IMAGES_URL,
      imageRequest, undefined, this.configService
    );
  }

  getProductImageRef(imageRequest: any, fetchAll: boolean) {

    return this.http.post<any>(this.api.Product.GET_PRODUCT_IMAGE_REF_URL +"buId="+this.envProperties.buId+"&fetchAll="+ fetchAll,imageRequest);
  }

  getProductRec(items) {
    let options = this.createRequestOptionsWithAccess();

    return this.http.post<any>(this.api.Product.GET_PRODUCT_RECOMMANDATION_URL,items);

  }


  createRequestOptionsWithAccess() {
    let headers = new HttpHeaders({
      "Content-Type": "application/json",
      "x-api-key": this.envProperties.apiKey,
      "access-token": this.appCacheService.get(CacheConstant.Auth_Tokens)["access-token"]
    });
    return headers;
  }
  getDetails(itemId) {
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    const defaultAccountuserDetailNum = this.userService.getDefaultAccount();
    
    const request = {
      productSearchCriteria: {
        shipToNum: defaultAccount.shipToNum,
        soldToNum: defaultAccount.soldToNum,
        searchVal: itemId,
        userDetailNum: defaultAccountuserDetailNum.userDetailNum
      }
    };

    return execute(this.http, this.api.Config.GET_PRODUCT_DETAILS_URL, request, undefined, this.configService);
  }

  getDetailsForUnAuthUser(itemId) {
    return this.http.post<any>(this.api.Product.GET_PRODUCT_DETAILS_FOR_UNAUTH_USER_URL + '/' + this.buid + '/' + itemId, null);
  }

  getHistory(itemId) {
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    const request = {
      shipToNum: defaultAccount.shipToNum,
      itemId: itemId
    };
    
    return execute(this.http, this.api.Config.GET_PRODUCT_PURCHASE_HISTORY_URL, request, undefined, this.configService);
  }

  getHistoryDetails() {
    const defaultAccountUserDetailNum = this.userService.getDefaultAccount();
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    const request = {
      shipToNum: defaultAccount.shipToNum,
      soldToNum: defaultAccount.soldToNum,
      userDetailNum: defaultAccountUserDetailNum.userDetailNum
    };
    return this.http.post<any>(
      this.api.Config.GET_PRODUCT_HISTORY_URL,
      request
    );
  }

  getSubs(request) {
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    const defaultAccountuserDetailNum = this.userService.getDefaultAccount();
    request.shipToNum = defaultAccount.shipToNum;
    request.soldToNum = defaultAccount.soldToNum;
    request.userDetailNum = defaultAccountuserDetailNum.userDetailNum;

    //return execute(this.http, this.api.Config.GET_PRODUCT_SUBSTITUTES_URL, request, undefined, this.configService);
    return this.http.post(this.api.Product.GET_SUBSTITUTION_URL, request,{ headers: { ignoreLoadingBar: '' }});
    
  }

  getPrebookProducts() {
    return this.http.post<any>(this.api.Order.GET_PREBOOK_PRODUCT_URL, null);
  }

  sendPrebookProducts(request: any) {
    return this.http.post<any>(
      this.api.Order.SUBMIT_PREBOOK_PRODUCT_URL,
      request
    );
  }

  showQuantityWarning(allocationCase: String): boolean {
    if (allocationCase === "Low Stock") return true;
    return false;
  }

  getItemsOnSaleFromProductHistoryDetails() {
    const defaultAccountUserDetailNum = this.userService.getDefaultAccount();
    const defaultAccount = this.userService.getDefaultAccSoldtoShipToObj();
    const request = {
      shipToNum: defaultAccount.shipToNum,
      soldToNum: defaultAccount.soldToNum,
      userDetailNum: defaultAccountUserDetailNum.userDetailNum
    };
    return this.http.post<any>(
      this.api.Config.GET_ITEMS_ON_SALE_FROM_PRODUCT_HISTORY_URL,
      request
    );
  }

  /***************Service Methods***************/

  private getOnHandStock(product: any) {
    let stock = 0;
  
    if (product.allocatedQuantity > 0) {
      stock = product.allocatedQuantity;
    }
  
    return stock;
  }

  /***************Pay Invoice Methods***************/

  
  getTotalAmountFromSubscription(request): Observable<any>{
  return this.http.post(this.api.Product.SUBSCRIPTION_DETAILS_URL, request,{ headers: { ignoreLoadingBar: '' }});
  }

  saveSubscription(request): Observable<any>{
    return this.http.post(this.api.Product.SAVE_SUBSCRIPTION_URL, request,{ headers: { ignoreLoadingBar: '' }});
  }

  getSubscribedItems(request): Observable<any>{
    return this.http.post(this.api.Product.GET_SUBSCRIBED_ITEMS_URL, request);
  }
  skipSubscription(value: number) {
    return this.http.post(this.api.Product.SKIP_SUBSCRIPTION_URL + value, { headers: { ignoreLoadingBar: '' } });
  }
  
  deleteSubscribedItems(request): Observable<any>{
    return this.http.post(this.api.Product.DELETE_SUBSCRIBED_ITEMS_URL, request);
  }

  editSubscription(request): Observable<any>{
    return this.http.post(this.api.Product.EDIT_SUBSCRIPTION_URL, request,{ headers: { ignoreLoadingBar: '' }});
  }

  searchSubscription(request): Observable<any>{
    return this.http.post(this.api.Product.SEARCH_SUBSCRIPTION_URL, request);
  }

  getCategories(): Observable<any>{
    return this.http.get(this.api.Product.GET_CATEGORY_LIST_URL);
  }

  getCategoryDetails(request) : Observable<any>{
    return this.http.post(this.api.Product.GET_CATEGORY_DETAILS_URL, request);
  }

  
  getProductSearch(searchRequest : any) {
    return this.http.post(this.api.Product.GET_PRODUCT_SEARCH_URL+ '/' +this.envProperties.buId + '/'+ searchRequest, null);
  }

  getProductFilterByCatalog(filterBy : string) {
    return this.http.post(this.api.Product.FILTER_PRODUCT_CATALOG_URL +this.envProperties.buId + `&filterBy=${filterBy}`, null);
  }

}
